import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Sidebar } from "./Sidebar";
import { TopBar, type BreadcrumbItem } from "./TopBar";
import { GlobalSearchModal } from "@/components/search/GlobalSearchModal";
import { useSidebarCollapsed } from "@/hooks/useSidebarState";

interface AppLayoutProps {
  children: ReactNode;
  breadcrumb?: BreadcrumbItem[];
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable
  );
}

export function AppLayout({ children, breadcrumb }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [searchOpen, setSearchOpen] = useState(false);

  const openSearch = useCallback(() => setSearchOpen(true), []);

  // Global shortcuts: Ctrl/Cmd+K toggles search anywhere; "/" opens it only
  // when the user is not typing in a field and the modal is closed.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
        return;
      }
      if (
        e.key === "/" &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !isTypingTarget(e.target)
      ) {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="flex h-screen bg-[hsl(210,30%,98%)]">
      {!collapsed && <Sidebar />}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar
          breadcrumb={breadcrumb}
          onToggleSidebar={() => setCollapsed((v) => !v)}
          onOpenSearch={openSearch}
        />
        <main className="flex-1 overflow-y-auto bg-[hsl(210,30%,98%)] p-6">
          {children}
        </main>
      </div>
      <GlobalSearchModal open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
